import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Heart, ShieldCheck, HandHeart, Sparkles, GraduationCap, TreePine, Users, LifeBuoy, Stethoscope, Milk, Quote } from 'lucide-react';
import { api, pickLocale } from '@/lib/api';
import { UKGM_HERO_BANNER_URL } from '@/lib/ui';
import { useSEO } from '@/lib/useSEO';

const PILLAR_ICONS = {
  'elderly-care': HandHeart, 'animal-welfare': Heart, 'cow-care': Milk, 'healthcare': Stethoscope,
  'education': GraduationCap, 'environment': TreePine, 'community': Users, 'disaster-relief': LifeBuoy,
};

const IMPACT_STATS = [
  { label: 'Elderly Lives Supported', value: 0, suffix: '+' },
  { label: 'Animals Protected', value: 0, suffix: '+' },
  { label: 'Trees Planted', value: 0, suffix: '+' },
  { label: 'Volunteers', value: 0, suffix: '+' },
  { label: 'Beneficiaries', value: 0, suffix: '+' },
];

const TESTIMONIALS = [
  { role: 'CSR Partner', body: 'A CSR endorsement will appear here once published by our corporate partner.', name: 'Awaiting release' },
  { role: 'HNI Donor', body: 'A philanthropist message will be shared here soon.', name: 'Awaiting release' },
  { role: 'Beneficiary Voice', body: 'A testimonial from a beneficiary of UKGM programmes will appear here.', name: 'Awaiting release' },
];

export default function Home() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: async () => (await api.get('/projects')).data });
  useSEO({ title: 'Home', description: 'Universal Krishna Gaukul Mission (UKGM) — A Registered Public Charitable Trust in India. Elderly care, cow care, animal welfare, environment conservation & social welfare.' });

  return (
    <div data-testid="home-page">
      {/* HERO — Official UKGM Banner (compressed by ~18% vs previous) */}
      <section className="relative fade-in" style={{ background: 'linear-gradient(180deg, #0F1E4A 0%, #172554 55%, #1E3A8A 100%)' }} data-testid="hero-banner-section">
        <h1 className="sr-only">Universal Krishna Gaukul Mission — A Registered Public Charitable Trust in India</h1>
        <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 pt-5 md:pt-8 pb-6 md:pb-10">
          <img
            src={UKGM_HERO_BANNER_URL}
            alt="Universal Krishna Gaukul Mission — Registered Public Trust. Elderly Care · Cow Care · Animal Welfare · Environment Conservation · Social Welfare."
            className="w-full h-auto rounded-2xl md:rounded-3xl shadow-2xl border border-ukgm-gold/40 max-h-[65vh] lg:max-h-[55vh] object-contain bg-[#0F1E4A]"
            loading="eager"
            fetchPriority="high"
            data-testid="hero-banner-image"
          />
          <div className="mt-5 md:mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
            <Link to="/donate" className="inline-flex items-center justify-center gap-2 bg-ukgm-gold text-ukgm-blue font-bold text-base px-8 py-3.5 rounded-full gold-glow" data-testid="hero-donate-btn">
              <Heart className="w-5 h-5" fill="currentColor" /> Donate Now
            </Link>
            <Link to="/projects" className="inline-flex items-center justify-center gap-2 bg-white/10 text-white border border-ukgm-gold/60 font-bold text-base px-8 py-3.5 rounded-full hover:bg-ukgm-gold hover:text-ukgm-blue" data-testid="hero-projects-btn">
              <Sparkles className="w-5 h-5 text-ukgm-gold" /> Our Programmes <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-4 flex justify-center">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-widest uppercase text-ukgm-gold/90 font-semibold border border-ukgm-gold/30 rounded-full px-4 py-1.5 bg-white/5">
              <ShieldCheck className="w-3.5 h-3.5" /> 80G · 12A · CSR-1 · NGO Darpan
            </div>
          </div>
        </div>
      </section>

      {/* IMPACT COUNTERS */}
      <section className="py-14 md:py-20 bg-white border-b border-ukgm-gold/20" data-testid="impact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">Our Impact</div>
            <h2 className="mt-2 font-serif text-3xl md:text-5xl text-ukgm-blue font-semibold">Building measurable change</h2>
            <p className="mt-3 text-ukgm-slate max-w-2xl mx-auto">Verified numbers are updated as UKGM programme reports are released.</p>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-4">
            {IMPACT_STATS.map((s, i) => (
              <div key={s.label} className="text-center bg-ukgm-cream rounded-2xl border border-ukgm-gold/25 p-6 card-lift" data-testid={`impact-${i}`}>
                <div className="font-serif text-3xl md:text-4xl text-ukgm-blue font-semibold">{s.value.toLocaleString('en-IN')}<span className="text-ukgm-gold">{s.suffix}</span></div>
                <div className="mt-2 text-[11px] tracking-widest uppercase text-ukgm-slate font-bold">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="py-14 md:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">{t('mission.eyebrow')}</div>
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-ukgm-gold/25 bg-white p-8">
            <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">{t('mission.vision_label')}</div>
            <p className="mt-4 font-serif text-2xl md:text-3xl text-ukgm-blue leading-snug">{t('mission.vision_body')}</p>
          </div>
          <div className="rounded-2xl bg-ukgm-blue text-white p-8">
            <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">{t('mission.mission_label')}</div>
            <p className="mt-4 font-serif text-2xl md:text-3xl text-white leading-snug">{t('mission.mission_body')}</p>
          </div>
        </div>
      </section>

      {/* PROGRAMMES */}
      <section className="py-14 md:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">{t('projects.eyebrow')}</div>
            <h2 className="mt-3 font-serif text-3xl md:text-5xl text-ukgm-blue font-semibold">{t('projects.title')}</h2>
          </div>
          <Link to="/projects" className="hidden md:inline-flex items-center gap-2 text-ukgm-blue font-semibold hover:text-ukgm-gold text-sm">{t('projects.cta_all')} <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {projects.map(p => {
            const Icon = PILLAR_ICONS[p.category] || Sparkles;
            return (
              <Link key={p.slug} to={`/projects/${p.slug}`} className="group bg-white rounded-2xl border border-ukgm-gold/20 p-6 card-lift h-full flex flex-col" data-testid={`project-card-${p.slug}`}>
                <div className="w-10 h-10 rounded-full bg-ukgm-blue text-ukgm-gold flex items-center justify-center"><Icon className="w-4 h-4" /></div>
                <div className="mt-4 text-[10px] tracking-widest uppercase text-ukgm-gold font-bold">{t(`projects.categories.${p.category}`)}</div>
                <h3 className="mt-2 font-serif text-lg text-ukgm-blue font-semibold group-hover:text-ukgm-gold leading-snug">{pickLocale(p.title, lang)}</h3>
                <p className="mt-2 text-sm text-ukgm-slate leading-relaxed line-clamp-3 flex-1">{pickLocale(p.summary, lang)}</p>
                <div className="mt-4 text-xs text-ukgm-gold font-semibold inline-flex items-center gap-1">{t('projects.view')} <ArrowRight className="w-3.5 h-3.5" /></div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-14 md:py-20 bg-ukgm-cream border-y border-ukgm-gold/20" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">Voices of Support</div>
            <h2 className="mt-2 font-serif text-3xl md:text-5xl text-ukgm-blue font-semibold">What our partners say</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((tItem, i) => (
              <div key={i} className="bg-white rounded-2xl border border-ukgm-gold/25 p-7 card-lift relative" data-testid={`testimonial-${i}`}>
                <Quote className="w-8 h-8 text-ukgm-gold/50" />
                <p className="mt-4 text-ukgm-charcoal italic leading-relaxed">"{tItem.body}"</p>
                <div className="mt-6 pt-4 border-t border-ukgm-gold/20">
                  <div className="font-serif text-ukgm-blue font-semibold">{tItem.name}</div>
                  <div className="text-[11px] tracking-widest uppercase text-ukgm-gold font-bold">{tItem.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE STRIP */}
      <section className="py-14 bg-ukgm-blue text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-6">
            <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">{t('trust.eyebrow')}</div>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl font-semibold">{t('trust.title')}</h2>
            <p className="mt-4 text-white/80">{t('trust.body')}</p>
            <Link to="/transparency" className="mt-6 inline-flex items-center gap-2 bg-ukgm-gold text-ukgm-blue font-bold px-6 py-3 rounded-full">
              View Transparency Dashboard <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[['PAN','AABTU5738J'],['CSR-1','CSR00106731'],['NGO Darpan','MH/2025/0872011'],['80G','Approved'],['12A','Approved'],['Reg. Act','Bombay PT Act 1950']].map(([l,v]) => (
              <div key={l} className="bg-white/10 border border-ukgm-gold/40 rounded-2xl px-4 py-4 text-center">
                <div className="text-ukgm-gold font-bold text-sm">{l}</div>
                <div className="text-xs text-white/80 mt-1 break-all">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
